import { Box, SvgIcon, IconButton } from '@mui/material'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import React from 'react'

import FilterIcon from '@hapana/shared/web/styles/assets/svgs/filter-small.svg'
import ClearSearchIcon from '@hapana/shared/web/styles/assets/svgs/times-circle.svg'
import TableGlobalFilter from '../table-global-filter/table-global-filter.component'
import TableFilter from '../table-filter.component'
import { FILTER_MODES } from '../../../table.constants'
import Button from '../../../../button/button.component'
import { BUTTON_VARIANTS } from '../../../../button/button.constants'

import { styles } from '../../../table.styles'
import { tableFilterDesktopStyles } from './table-filter-desktop.styles'

const TableDesktopFilters = ({
  columns,
  state,
  handleGlobalFilter,
  searchPlaceholder,
  hasGlobalFilter,
  clearFilters,
  activeFilterCount,
  tableColumns,
  handleColumnFilter,
  filterMode,
  shouldClearFilters,
  setShouldClearFilters,
  isFiltersLoading,
  muiSelectProps,
  initialValue,
  setPage,
  isSearchDisabled,
  areAllFiltersDisabled,
  searchSlot,
  hasToolbarWithBorder,
  searchEndAdornment,
  showSearchFieldBorder,
  onKeyDown,
  onSearchIconClick,
  onClearSearchRef,
  contentAfterSearch,
  searchIconPosition,
}) => {
  const { t } = useTranslation('common')

  return (
    <Box sx={styles.filtersContainer}>
      {hasGlobalFilter && (
        <Box
          sx={[
            styles.globalClientSideFilter,
            (theme) =>
              tableFilterDesktopStyles.globalClientSideBorder(theme, { hasToolbarWithBorder }),
          ]}
        >
          <TableGlobalFilter
            globalFilter={state.globalFilter}
            onFilter={handleGlobalFilter}
            placeholder={searchPlaceholder}
            hasFilters={!!columns.find((item) => item.hasFilter)}
            initialValue={initialValue}
            setPage={setPage}
            isDisabled={isSearchDisabled}
            searchSlot={searchSlot}
            hasToolbarWithBorder={hasToolbarWithBorder}
            searchEndAdornment={searchEndAdornment}
            showSearchFieldBorder={showSearchFieldBorder}
            onKeyDown={onKeyDown}
            onSearchIconClick={onSearchIconClick}
            onClearSearchRef={onClearSearchRef}
            searchIconPosition={searchIconPosition}
            filterMode={filterMode}
          />
        </Box>
      )}
      {contentAfterSearch && <Box sx={styles.contentAfterSearch}>{contentAfterSearch}</Box>}
      <Box sx={[styles.filtersRoot, !hasGlobalFilter && styles.filtersTopBorder]}>
        {columns.find((item) => item.hasFilter) && (
          <Box sx={[styles.columnsFilters, styles.fullWidth]}>
            {activeFilterCount > 0 ? (
              <Box sx={[styles.clearFilterContainer]}>
                <Button
                  variant={BUTTON_VARIANTS.TEXT}
                  startIcon={ClearSearchIcon}
                  test-id={t('components.table.filters.clear')}
                  label={t('components.table.filters.clear')}
                  sx={styles.clearFilterButton}
                  onClick={clearFilters}
                />
              </Box>
            ) : (
              <Box sx={styles.filterLabelDesktopContainer} className="filterLabelDesktopContainer">
                <IconButton sx={styles.iconButton} disabled={areAllFiltersDisabled}>
                  <SvgIcon component={FilterIcon} />
                </IconButton>
              </Box>
            )}
            {tableColumns
              .filter((item) => item.filterSelectType || item.filterComponent || item.filter)
              .map((item) => (
                <TableFilter
                  key={item.id}
                  column={item}
                  setFilter={handleColumnFilter}
                  filterMode={filterMode}
                  filterSelectType={item.filterSelectType}
                  shouldClearFilters={shouldClearFilters}
                  setShouldClearFilters={setShouldClearFilters}
                  hasCustomFilterHeader={item.hasCustomFilterHeader}
                  isFiltersLoading={isFiltersLoading}
                  muiSelectProps={muiSelectProps}
                  disabled={item.disabled}
                />
              ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}

TableDesktopFilters.propTypes = {
  columns: PropTypes.array.isRequired,
  state: PropTypes.object.isRequired,
  handleGlobalFilter: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string,
  hasGlobalFilter: PropTypes.bool,
  clearFilters: PropTypes.func.isRequired,
  activeFilterCount: PropTypes.number.isRequired,
  tableColumns: PropTypes.array.isRequired,
  handleColumnFilter: PropTypes.func.isRequired,
  filterMode: PropTypes.oneOf(Object.values(FILTER_MODES)),
  shouldClearFilters: PropTypes.bool.isRequired,
  setShouldClearFilters: PropTypes.func.isRequired,
  isFiltersLoading: PropTypes.bool,
  muiSelectProps: PropTypes.object,
  initialValue: PropTypes.string,
  setPage: PropTypes.func,
  isSearchDisabled: PropTypes.bool,
  areAllFiltersDisabled: PropTypes.bool,
  searchSlot: PropTypes.node,
  hasToolbarWithBorder: PropTypes.bool,
  showSearchFieldBorder: PropTypes.bool,
  searchEndAdornment: PropTypes.node,
  onKeyDown: PropTypes.func,
  onSearchIconClick: PropTypes.func,
  onClearSearchRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.func }),
  ]),
  contentAfterSearch: PropTypes.node,
  searchIconPosition: PropTypes.oneOf(['start', 'end']),
}

export default TableDesktopFilters
